import helperFunctions from "@/components/RenderedDie/shaders/helperFunctions.frag";
import { shaderMaterial } from "@react-three/drei";
import { extend } from "@react-three/fiber";
import { Color, Vector3 } from "three";

/**
 * This is shader code exported from the Liquid material
 * built with the Babylon node material editor.
 *
 * The uniforms were initially set using the same defaults as in the editor
 * and then adjusted as needed.
 */
export const LiquidMaterial = shaderMaterial(
  {
    u_TopColor: new Color("rgb(255, 0, 0)"),
    u_BottomColor: new Color("rgb(189, 0, 0)"),
    u_Color: new Color("rgb(255, 0, 0)"),
    u_ObjectPosition: new Vector3(0, 0, 0),
    u_Float1: 1.8, // general ripple effect size
    u_Float2: 0.4,
    iGlobalTime: 0.0,
    u_Float3: 0.18,
    u_Float4: 2.5, // bubble size
    u_Float5: 0.56,
    u_Float6: 5.0,
    u_Float7: 0.1, // top bubble appearance (shows dark at the bottom, but only in previewer)
    u_Color1: new Color("rgb(239, 1, 1)"),
    u_Float8: 0.2, // bubble speed
    u_Float9: 1.8, // how much bubbles show at edge
    u_Float10: 0.79,
    u_Float11: 1.0,
    u_Float12: 5.0,
    u_Float13: 0.06,
    u_Cut: 1.2, // how high up the material reaches in world units
    u_Float14: 0.5, // ripple amount
    u_Float15: 1.0,
    u_Intensity: 0.37,
    u_Float16: -1.3, // liquid color mixing
    u_Color2: new Color("rgb(255, 26, 26)"),
    u_WobbleX: 0.0,
    u_WobbleZ: 0.0,
    u_UpAxis: new Vector3(0, 1, 0), // added to account for previewer vs rolling orientation
  },
  `
    // Vertex shader
    #if defined(WEBGL2) || defined(WEBGPU)
    precision highp sampler2DArray;
    #endif
    precision highp float;

    //Uniforms
    uniform vec3 u_TopColor;
    uniform vec3 u_BottomColor;
    uniform vec3 u_Color;
    uniform vec3 u_ObjectPosition;
    uniform float u_Float1;
    uniform float u_Float2;
    uniform float iGlobalTime;
    uniform float u_Float3;
    uniform float u_Float4;
    uniform float u_Float5;
    uniform float u_Float6;
    uniform float u_Float7;
    uniform vec3 u_Color1;
    uniform float u_Float8;
    uniform float u_Float9;
    uniform float u_Float10;
    uniform float u_Float11;
    uniform float u_Float12;
    uniform float u_Float13;
    uniform float u_Cut;
    uniform float u_Float14;
    uniform float u_Float15;
    uniform float u_Intensity;
    uniform float u_Float16;
    uniform vec3 u_Color2;
    uniform float u_WobbleX;
    uniform float u_WobbleZ;
    uniform vec3 u_UpAxis;

    //Varyings
    varying vec3 v_xyz1;

    //Constants
    const float u_Float = 0.36;


    //Entry point
    void main(void) {
        // Use Three.js built-in matrices instead of custom uniforms
        // World position in world space
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        v_xyz1 = worldPos.xyz;
        // Project to clip space (modelViewMatrix already equals viewMatrix * modelMatrix)
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPosition;

    }
`,
  `
    // Fragment shader
    #if defined(PREPASS)
    #extension GL_EXT_draw_buffers : require
    layout(location = 0) out highp vec4 glFragData[SCENE_MRT_COUNT];
    highp vec4 gl_FragColor;
    #endif
    #if defined(WEBGL2) || defined(WEBGPU)
    precision highp sampler2DArray;
    #endif
    precision highp float;

    //Uniforms
    uniform vec3 u_TopColor;
    uniform vec3 u_BottomColor;
    uniform vec3 u_Color;
    uniform vec3 u_ObjectPosition;
    uniform float u_Float1;
    uniform float u_Float2;
    uniform float iGlobalTime;
    uniform float u_Float3;
    uniform float u_Float4;
    uniform float u_Float5;
    uniform float u_Float6;
    uniform float u_Float7;
    uniform vec3 u_Color1;
    uniform float u_Float8;
    uniform float u_Float9;
    uniform float u_Float10;
    uniform float u_Float11;
    uniform float u_Float12;
    uniform float u_Float13;
    uniform float u_Cut;
    uniform float u_Float14;
    uniform float u_Float15;
    uniform float u_Intensity;
    uniform float u_Float16;
    uniform vec3 u_Color2;
    uniform float u_WobbleX;
    uniform float u_WobbleZ;
    uniform vec3 u_UpAxis;

    //Varyings
    varying vec3 v_xyz1;

    // SimplexPerlin3D
    const float SKEWFACTOR = 1.0/3.0;
    const float UNSKEWFACTOR = 1.0/6.0;
    const float SIMPLEX_CORNER_POS = 0.5;
    const float SIMPLEX_TETRAHADRON_HEIGHT = 0.70710678118654752440084436210485;
    float SimplexPerlin3D( vec3 source ){
        vec3 P = source;
        P.x = P.x == 0. && P.y == 0. && P.z == 0.  ?  0.00001  :  P.x;
        P *= SIMPLEX_TETRAHADRON_HEIGHT;
        vec3 Pi = floor( P + dot( P, vec3( SKEWFACTOR) ) );    vec3 x0 = P - Pi + dot(Pi, vec3( UNSKEWFACTOR ) );
        vec3 g = step(x0.yzx, x0.xyz);
        vec3 l = 1.0 - g;
        vec3 Pi_1 = min( g.xyz, l.zxy );
        vec3 Pi_2 = max( g.xyz, l.zxy );
        vec3 x1 = x0 - Pi_1 + UNSKEWFACTOR;
        vec3 x2 = x0 - Pi_2 + SKEWFACTOR;
        vec3 x3 = x0 - SIMPLEX_CORNER_POS;
        vec4 v1234_x = vec4( x0.x, x1.x, x2.x, x3.x );
        vec4 v1234_y = vec4( x0.y, x1.y, x2.y, x3.y );
        vec4 v1234_z = vec4( x0.z, x1.z, x2.z, x3.z );
        Pi = Pi.xyz - floor(Pi.xyz * ( 1.0 / 69.0 )) * 69.0;
        vec3 Pi_inc1 = step( Pi, vec3( 69.0 - 1.5 ) ) * ( Pi + 1.0 );
        vec4 Pt = vec4( Pi.xy, Pi_inc1.xy ) + vec2( 50.0, 161.0 ).xyxy;
        Pt *= Pt;
        vec4 V1xy_V2xy = mix( Pt.xyxy, Pt.zwzw, vec4( Pi_1.xy, Pi_2.xy ) );
        Pt = vec4( Pt.x, V1xy_V2xy.xz, Pt.z ) * vec4( Pt.y, V1xy_V2xy.yw, Pt.w );
        const vec3 SOMELARGEFLOATS = vec3( 635.298681, 682.357502, 668.926525 );
        const vec3 ZINC = vec3( 48.500388, 65.294118, 63.934599 );
        vec3 lowz_mods = vec3( 1.0 / ( SOMELARGEFLOATS.xyz + Pi.zzz * ZINC.xyz ) );
        vec3 highz_mods = vec3( 1.0 / ( SOMELARGEFLOATS.xyz + Pi_inc1.zzz * ZINC.xyz ) );
        Pi_1 = ( Pi_1.z < 0.5 )  ?  lowz_mods  :  highz_mods;
        Pi_2 = ( Pi_2.z < 0.5 )  ?  lowz_mods  :  highz_mods;
        vec4 hash_0 = fract( Pt * vec4( lowz_mods.x, Pi_1.x, Pi_2.x, highz_mods.x ) ) - 0.49999;
        vec4 hash_1 = fract( Pt * vec4( lowz_mods.y, Pi_1.y, Pi_2.y, highz_mods.y ) ) - 0.49999;
        vec4 hash_2 = fract( Pt * vec4( lowz_mods.z, Pi_1.z, Pi_2.z, highz_mods.z ) ) - 0.49999;
        vec4 grad_results = inversesqrt( hash_0 * hash_0 + hash_1 * hash_1 + hash_2 * hash_2 ) * ( hash_0 * v1234_x + hash_1 * v1234_y + hash_2 * v1234_z );
        const float FINAL_NORMALIZATION = 37.837227241611314102871574478976;
        vec4 kernel_weights = v1234_x * v1234_x + v1234_y * v1234_y + v1234_z * v1234_z;
        kernel_weights = max(0.5 - kernel_weights, vec4(0.));
        kernel_weights = kernel_weights*kernel_weights*kernel_weights;
        return dot( kernel_weights, grad_results ) * FINAL_NORMALIZATION;
    }

    //FragmentOutput
    ${helperFunctions}

    //Constants
    const float u_Float = 0.36;


    //Entry point
    void main(void) {

    //Front facing
    float output5 = (gl_FrontFacing) ? 1.0 : 0.0;

    //Step
    float output4 = step(output5, u_Float);

    //Scale
    vec3 output3 = u_TopColor * output4;

    //Subtract
    vec3 output18 = v_xyz1 - u_ObjectPosition;

    //Scale
    vec3 output17 = output18 * u_Float1;

    // Build a stable local frame from the configured up axis so all
    // procedural sampling stays consistent across preview and rolling canvases.
    vec3 up = normalize(u_UpAxis);
    vec3 fallback = abs(up.y) < 0.999 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    vec3 right = normalize(cross(fallback, up));
    vec3 forward = normalize(cross(up, right));
    float localX = dot(output17, right);
    float localY = dot(output17, up);
    float localZ = dot(output17, forward);

    //VectorSplitter
    float x = localX;
    float y = localY;
    float vertical = localY;
    float z = localZ;

    //Scale
    float output20 = vertical * u_Float2;

    //Multiply
    float output21 = iGlobalTime * u_Float3;

    //Subtract
    float output19 = output20 - output21;

    //VectorMerger
    vec3 xyz2 = vec3(x, output19, z).xyz;

    //Scale
    vec3 output16 = xyz2 * u_Float4;

    //SimplexPerlin3D
    float output15 = SimplexPerlin3D(output16);

    //Step
    float output14 = step(u_Float5, output15);

    //Multiply
    float output13 = output14 * u_Float6;

    //Scale
    vec3 output12 = u_Color * output13;

    //Scale
    vec3 output11 = output12 * u_Float7;

    //Multiply
    float output30 = iGlobalTime * u_Float8;

    //Subtract
    float output29 = output20 - output30;

    //VectorMerger
    vec3 xyz4 = vec3(x, output29, z).xyz;

    //Scale
    vec3 output28 = xyz4 * u_Float9;

    //SimplexPerlin3D
    float output27 = SimplexPerlin3D(output28);

    //Step
    float output26 = step(u_Float10, output27);

    //Scale
    float output25 = output26 * u_Float11;

    //Multiply
    float output24 = output25 * u_Float12;

    //Scale
    vec3 output23 = u_Color1 * output24;

    //Scale
    vec3 output22 = output23 * u_Float13;

    //Add
    vec3 output10 = output11 + output22;

    //Add
    vec3 output9 = u_BottomColor + output10;

    //One minus
    float output31 = 1. - output4;

    //Scale
    vec3 output8 = output9 * output31;

    //VectorSplitter
    float x1 = localX;
    float y1 = localY;
    float vertical1 = localY;
    float z1 = localZ;

    //VectorSplitter
    float x2 = localX;
    float y2 = localY;
    float vertical2 = localY;
    float z2 = localZ;

    //Scale
    float output39 = x2 * u_Float14;

    //Multiply
    float output40 = u_Float15 * iGlobalTime;

    //Add
    float output38 = output39 + output40;

    //Scale
    float output42 = vertical2 * u_Float14;

    //Add
    float output41 = output42 + output40;

    //Scale
    float output44 = z2 * u_Float14;

    //Add
    float output43 = output44 + output40;

    //VectorMerger
    vec3 xyz6 = vec3(output38, output41, output43).xyz;

    //SimplexPerlin3D
    float output37 = SimplexPerlin3D(xyz6);

    //Multiply
    float output36 = output37 * u_Intensity;

    //Abs
    float output35 = abs(output36);

    //Add
    float output34 = u_Cut + output35;

    //Subtract
    float output33 = vertical1 - output34;

    //Subtract
    float output32 = output33 - u_Float16;

    //Scale
    vec3 output7 = output8 * output32;

    //One minus
    float output46 = 1. - output32;

    //Scale
    vec3 output45 = u_Color2 * output46;

    //Add
    vec3 output6 = output7 + output45;

    //Add
    vec3 output2 = output3 + output6;

    //FragmentOutput
    gl_FragColor  = vec4(output2, 1.0);
    #ifdef CONVERTTOLINEAR0
    gl_FragColor  = toLinearSpace(gl_FragColor);
    #endif
    #ifdef CONVERTTOGAMMA0
    gl_FragColor  = toGammaSpace(gl_FragColor);
    #endif
    #if defined(PREPASS)
    gl_FragData[0] = gl_FragColor;
    #endif

    //Multiply
    float output50 = x1 * u_WobbleX;

    //Multiply
    float output51 = z1 * u_WobbleZ;

    //Add
    float output49 = output50 + output51;

    //Add
    float output48 = output49 + output34;

    //Step
    float output47 = step(vertical1, output48);

    //Discard
    if (output47 < u_Float) { discard; }

    }
  `,
);

extend({ LiquidMaterial });
